import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Container } from "reactstrap";
import { ChartMetric } from "../../../cores/analytics/interface/ChartMetric";
import { ChartContainer } from "./ChartContainer";
import { LineChart } from "./charts/LineChart";
import { TikTokAnalyticsSnapshot } from "./tiktokAnalyticsMock";
import { TIKTOK_BLACK, TIKTOK_RED } from "./tiktokBrandColors";

interface Props {
  snapshot: TikTokAnalyticsSnapshot;
}

const toChart = (
  days: { label: string; value: number }[],
  label: string,
  color: string
): ChartMetric => ({
  labels: days.map((day) => day.label),
  datasets: [
    {
      label,
      color,
      data: days.map((day) => day.value),
      yAxisID: "left",
      fill: false,
      type: "line",
    },
  ],
});

export const TikTokReportCharts = ({ snapshot }: Props) => {
  const intl = useIntl();

  const followersChart = useMemo(
    () =>
      toChart(
        snapshot.growth.followersSeries.map((day) => ({
          label: day.label,
          value: day.followerTotal ?? 0,
        })),
        intl.formatMessage({ id: "app.tiktokAnalytics.followers" }),
        TIKTOK_BLACK
      ),
    [intl, snapshot.growth.followersSeries]
  );

  const viewsChart = useMemo(
    () =>
      toChart(
        snapshot.postsPeriod.viewsSeries.map((day) => ({
          label: day.label,
          value: day.views ?? 0,
        })),
        intl.formatMessage({ id: "app.tiktokAnalytics.views" }),
        TIKTOK_RED
      ),
    [intl, snapshot.postsPeriod.viewsSeries]
  );

  const interactionsChart = useMemo(
    () =>
      toChart(
        snapshot.postsPeriod.interactionsSeries.map((day) => ({
          label: day.label,
          value: day.interactions ?? 0,
        })),
        intl.formatMessage({ id: "app.tiktokAnalytics.interactions" }),
        TIKTOK_BLACK
      ),
    [intl, snapshot.postsPeriod.interactionsSeries]
  );

  return (
    <Container fluid className="px-0">
      <ChartContainer
        id="tiktokChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.tiktokAnalytics.sectionGrowth" })}
      >
        <LineChart {...followersChart} />
      </ChartContainer>

      <ChartContainer
        id="tiktokChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.tiktokAnalytics.views" })}
      >
        <LineChart {...viewsChart} />
      </ChartContainer>

      <ChartContainer
        id="tiktokChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.tiktokAnalytics.interactions" })}
      >
        <LineChart {...interactionsChart} />
      </ChartContainer>
    </Container>
  );
};
